import React from 'react'
import { Download, BoxArrowUpRight, FileEarmark, FileEarmarkImage, FileEarmarkMusic, FileEarmarkPdf, FileEarmarkText, FileEarmarkWord } from 'react-bootstrap-icons'

const ICON_BY_EXTENSION = {
  heic: FileEarmarkImage,
  mp3: FileEarmarkMusic,
  pdf: FileEarmarkPdf,
  doc: FileEarmarkWord,
  docx: FileEarmarkWord,
  txt: FileEarmarkText,
}

// Per-asset download/open-in-new-tab controls, overlaid on the tile itself -
// with multiple assets per version there's no single URL a shared pair of
// buttons could act on, so each tile gets its own. stopPropagation keeps a
// click here from also triggering the tile's own onClick/anchor (relevant
// for the file-icon variant below, which is itself a link).
function AssetActions({ asset }) {
  // In staging/production asset.url is a direct S3 link, and browsers
  // ignore the `download` attribute on cross-origin anchors - without
  // routing through our own origin this just opens the file instead of
  // saving it. /proxy/image already exists to work around S3 CORS
  // elsewhere; `download=1` makes it respond with Content-Disposition:
  // attachment instead of its default inline render. In development
  // CarrierWave serves local files same-origin, so asset.url is already a
  // relative path there and the plain `download` attribute works as-is -
  // the proxy only accepts absolute S3 URLs, so it must be skipped for that case.
  const downloadUrl = /^https?:\/\//i.test(asset.url)
    ? `/proxy/image?url=${encodeURIComponent(asset.url)}&download=1`
    : asset.url

  return (
    <div className="absolute top-2 right-2 flex gap-1.5">
      <a
        href={downloadUrl}
        download
        onClick={e => e.stopPropagation()}
        title="Download"
        className="flex items-center justify-center h-9 w-9 rounded-lg bg-black/50 backdrop-blur-sm text-white text-lg hover:bg-black/70"
      >
        <Download />
      </a>
      <a
        href={asset.url}
        target="_blank"
        rel="noreferrer"
        onClick={e => e.stopPropagation()}
        title="Open Asset"
        className="flex items-center justify-center h-9 w-9 rounded-lg bg-black/50 backdrop-blur-sm text-white text-lg hover:bg-black/70"
      >
        <BoxArrowUpRight />
      </a>
    </div>
  )
}

// Renders whatever a DraftAsset actually is - a real inline <video>/<img> for
// the formats browsers display natively, or a clickable file-type icon for
// everything else (HEIC/MP3/PDF/DOC/DOCX/TXT). MP3 is deliberately an icon
// rather than an embedded <audio> player - the native controls need real
// width to render play/timeline/volume, and collapse into an unusable
// three-dot "more options" button at thumbnail size; opening the file in a
// new tab plays it just fine without that constraint. `asset.url` always
// points at the original uploaded file - the Drafts feature never generates
// or reads a thumbnail version (see DraftUploader's own comment on this).
// `showActions` opts in to the download/open overlay - off by default so
// callers that don't want it (e.g. Version History's compact thumbnails)
// keep their current look.
export default function DraftAssetPreview({ asset, className = '', showActions = false }) {
  const type = asset.file_type?.toLowerCase()

  if (['mp4', 'mov'].includes(type)) {
    return (
      <div className="relative">
        <video src={asset.url} controls className={className} />
        {showActions && <AssetActions asset={asset} />}
      </div>
    )
  }

  if (['jpg', 'jpeg', 'png'].includes(type)) {
    return (
      <div className="relative">
        <img src={asset.url} className={className} />
        {showActions && <AssetActions asset={asset} />}
      </div>
    )
  }

  const Icon = ICON_BY_EXTENSION[type] || FileEarmark
  return (
    <div className="relative">
      <a
        href={asset.url}
        target="_blank"
        rel="noreferrer"
        className={`flex flex-col items-center justify-center gap-1 bg-rb-grey dark:bg-rb-dark-mode-lighter rounded-lg overflow-hidden p-2 ${className}`}
      >
        <Icon className="text-3xl text-rb-mat-grey dark:text-rb-dark-mode-text-grey shrink-0" />
        <span className="text-xs text-rb-mat-grey dark:text-rb-dark-mode-text-grey truncate w-full text-center">{asset.file_name}</span>
      </a>
      {showActions && <AssetActions asset={asset} />}
    </div>
  )
}
